import { useState } from "react";

import type { PerformanceReportBackLinkProps } from "./components/BackLink";
import type { PerformanceReportAnalytics } from "./types";

import { useIsMobile } from "../hooks/useIsMobile";
import { InfoIcon } from "../Icons";
import { PageSection } from "../PageSection";
import { SegmentControl } from "../SegmentControl";
import { ExportCSV, TableDesktop } from "../Table";
import { TableState } from "../TableState";
import { Typo } from "../Typo";
import { BackLink } from "./components/BackLink";
import { ColumnsPopover } from "./components/ColumnsPopover";
import { PerformanceControls } from "./components/PerformanceControls";
import { PerformanceFilterModal } from "./components/PerformanceFilterModal";
import {
  ControlsSkeleton,
  InsightsBarSkeleton,
  PageHeaderSkeleton,
} from "./components/PerformanceTableSkeleton";
import { PerformanceTableSkeleton } from "./components/PerformanceTableSkeleton";
import { SummaryCards } from "./components/SummaryCards";
import { usePerformanceReportTranslations } from "./hooks/usePerformanceReportTranslations";
import { MobilePerformanceReportPage } from "./mobile";
import {
  PerformanceReportAnalyticsProvider,
  usePerformanceReportAnalyticsContext,
} from "./providers/PerformanceReportAnalyticsProvider";
import { usePerformanceReportContext } from "./providers/PerformanceReportProvider";

export { PerformanceReportProvider } from "./providers/PerformanceReportProvider";
export {
  PerformanceReportAnalyticsProvider,
  usePerformanceReportAnalyticsContext,
} from "./providers/PerformanceReportAnalyticsProvider";
export { usePerformanceReportTranslations } from "./hooks/usePerformanceReportTranslations";
export {
  fmtCompactInt,
  fmtInt,
  fmtUsdNum,
  hasMetric,
  lastUpdatedFromSync,
  metricVal,
} from "./helpers";
export {
  buildPerformanceReportHref,
  campaignDeepLinkValue,
  decodeOrigin,
  PERFORMANCE_REPORT_ORIGINS,
} from "./mapping/buildPerformanceReportHref";
export { BackLink } from "./components/BackLink";
export { mapFilterOptions } from "./mapping/filterOptions";
export * from "./urlParams";
export {
  mapPerformanceBody,
  mapPerformanceRow,
} from "./mapping/mapPerformanceRow";
export {
  mapPerformanceStats,
  toStatsQueryParams,
  ZERO_KPI_CARDS,
} from "./mapping/mapPerformanceStats";
// `formatDate` is aliased because the package root already exports an
// unrelated `formatDate` (LeadsTable/Columns). It and `orUndefined` are public
// only for the portal's CSV export, which hand-builds its own params; prefer
// the assembled builders (`toQueryParams` / `toStatsQueryParams`) over calling
// these directly.
export {
  formatDate as formatPerformanceReportDate,
  NO_CAMPAIGN_FILTER_VALUE,
  orUndefined,
  toCampaignIds,
  toQueryParams,
} from "./mapping/queryParams";
export type {
  BuildPerformanceReportHrefParams,
  PerformanceReportOrigin,
} from "./mapping/buildPerformanceReportHref";
export type { PerformanceReportBackLinkProps } from "./components/BackLink";
export type { LanguageLabelResolver } from "./mapping/mapPerformanceRow";
export type {
  PerformanceReportInitialState,
  PerformanceReportResources,
} from "./providers/PerformanceReportProvider";
export type { PerformanceReportTranslations } from "./hooks/usePerformanceReportTranslations";
export type {
  DateRange,
  Filters,
  MetricCol,
  MetricKey,
  PerformanceReportAnalytics,
  PerformanceReportExportHandler,
  PerformanceReportExportRequest,
  PerformanceReportSelectOptions,
  PerformanceReportTableData,
  PerformanceReportTableParams,
  PerfRow,
  SourceType,
  SummaryCard,
  ViewTab,
} from "./types";

export type PerformanceReportPageProps = {
  /** Back-navigation link to the originating Marketing tab. Omitted (e.g. by
   * backoffice) renders no back link. */
  backLink?: PerformanceReportBackLinkProps;
  analytics?: PerformanceReportAnalytics;
};

export const PerformanceReportPage = ({
  analytics,
  backLink,
}: PerformanceReportPageProps) => (
  <PerformanceReportAnalyticsProvider analytics={analytics}>
    <PerformanceReportPageInner backLink={backLink} />
  </PerformanceReportAnalyticsProvider>
);

const PerformanceReportPageInner = ({
  backLink,
}: Pick<PerformanceReportPageProps, "backLink">) => {
  const translations = usePerformanceReportTranslations();
  const isMobile = useIsMobile();
  const analytics = usePerformanceReportAnalyticsContext();
  const {
    tab,
    setTab,
    sorting,
    setSorting,
    hidden,
    toggleColumn,
    filters,
    setFilters,
    kpiCards,
    columns,
    rows,
    tabOptions,
    csvFileName,
    csvDataFormatter,
    exportCsv,
    isExporting,
    canExport,
    filterFns,
    lastUpdated,
    selectOptions,
    isError,
    isLoading,
    isInitialLoading,
    isCardsLoading,
    isCardsError,
    isCardsEmpty,
    retryFetch,
    isOptionsLoading,
    onFilterOpen,
    page,
    pageSize,
    totalCount,
    setPage,
  } = usePerformanceReportContext();

  const [filterOpen, setFilterOpen] = useState(false);

  const pageCount = Math.ceil((totalCount || 0) / (pageSize || 10));

  if (isMobile) {
    return <MobilePerformanceReportPage backLink={backLink} />;
  }

  return (
    <PageSection>
      {/* Back-navigation link (deep-linked from Marketing). URL-param-driven,
          not fetch-driven, so it renders outside the isInitialLoading branch
          below — a back affordance must not disappear during load. */}
      <BackLink
        href={backLink?.href ?? ""}
        label={backLink?.label ?? ""}
        onClick={backLink?.onClick}
        onNavigate={backLink?.onNavigate}
      />

      {/* Page header (AC-1). On first entry the whole chrome is a skeleton; a
          later query change leaves it live and only reloads cards + table. */}
      {isInitialLoading ? (
        <PageHeaderSkeleton />
      ) : (
        <div className="flex flex-col gap-3">
          <Typo element="h1" type="heading4XL" textColor="text-text-primary">
            {translations.headerTitle}
          </Typo>
          <div className="flex items-center gap-2">
            <InfoIcon
              className="h-4 w-4 shrink-0 text-icon-brand-default [&_path]:stroke-icon-brand-default"
              aria-hidden="true"
              focusable="false"
            />
            <Typo element="span" type="bodySM" textColor="text-text-secondary">
              {translations.headerLastUpdated(lastUpdated)}
            </Typo>
          </div>
        </div>
      )}

      {/* Controls: date range + filter (AC-2, AC-3) */}
      {isInitialLoading ? (
        <ControlsSkeleton />
      ) : (
        <PerformanceControls
          onOpenFilter={() => {
            analytics?.trackFilterButtonEvent?.();
            onFilterOpen();
            setFilterOpen(true);
          }}
        />
      )}

      {/* Summary stat cards (AC-4) */}
      <SummaryCards
        cards={kpiCards}
        isLoading={isCardsLoading}
        isError={isCardsError}
        isEmpty={isCardsEmpty}
      />

      {/* Insights bar: view-by + columns + export (AC-5, AC-6, AC-7) */}
      {isInitialLoading ? (
        <InsightsBarSkeleton />
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          <Typo element="span" type="labelSM" textColor="text-text-secondary">
            {translations.viewBy}
          </Typo>
          <SegmentControl
            variant="filterButtons"
            options={tabOptions}
            value={tab}
            onChange={(next) => {
              analytics?.trackViewByEvent?.(next);
              setTab(next);
            }}
            aria-label={translations.viewBy}
          />
          <div className="ms-auto flex items-center gap-4">
            <ColumnsPopover hidden={hidden} onToggle={toggleColumn} />
            <ExportCSV
              buttonLabel={translations.exportButton}
              data={rows}
              columns={columns}
              dataFormatter={csvDataFormatter}
              sorting={sorting}
              fileName={csvFileName}
              filterFns={filterFns}
              onExport={
                canExport
                  ? () => {
                      analytics?.trackExportCsvEvent?.();
                      exportCsv();
                    }
                  : undefined
              }
              isExporting={isExporting}
              showIcon
            />
          </div>
        </div>
      )}

      {/* Data table (AC-8, AC-9, AC-10, AC-12) */}
      <TableDesktop
        className="!mt-0"
        skeletonRowHeight={74}
        scrollFrame
        data={rows}
        columns={columns}
        sorting={sorting}
        onSortingChange={setSorting}
        enableSorting
        enabledServerSideSorting
        showHorizontalScroll
        stickyFirstColumn
        // The engine preserves the last successful page of data alongside an
        // error, so without this the footer still reads "Showing 1-10 of 17"
        // directly beneath "Unable to load data".
        showPagination={!isError}
        enableServerSidePagination
        pageCount={pageCount}
        totalCount={totalCount}
        state={{ pagination: { pageIndex: page, pageSize } }}
        onPaginationChange={(updater) => {
          const next =
            typeof updater === "function"
              ? updater({ pageIndex: page, pageSize })
              : updater;
          setPage(next.pageIndex);
        }}
        isLoading={isLoading}
        isError={isError}
        filterFns={filterFns}
        skeletonComponent={
          <PerformanceTableSkeleton
            columns={columns}
            tab={tab}
            stickyFirstColumn
          />
        }
        onRetry={retryFetch}
        errorComponent={
          <TableState
            variant="error"
            title={translations.errorTitle}
            description={translations.errorText}
            actionLabel={translations.errorActionText}
            onAction={retryFetch}
          />
        }
        translatedContent={{
          noDataFoundTitle: translations.emptyTitle,
          noDataFoundText: translations.emptyText,
          noResultsFoundText: translations.emptyText,
          paginationLabelForShowing: translations.paginationLabelForShowing,
          paginationLabelForOf: translations.paginationLabelForOf,
        }}
      />

      <PerformanceFilterModal
        isOpen={filterOpen}
        initial={filters}
        selectOptions={selectOptions}
        isOptionsLoading={isOptionsLoading}
        onApply={(next) => {
          setFilters(next);
          setFilterOpen(false);
        }}
        onClose={() => setFilterOpen(false)}
      />
    </PageSection>
  );
};
